import config from '@/config'
import { httpClientPrivate } from '@/libs/httpClient'
import { useQuery } from '@tanstack/react-query'
import { useDebounce } from '@/hooks/useDebounce'
import { DiseasesResponseType } from './types'

const fetchDiseases = async (query: { page: number; limit: number; search: string }) => {
  const response = await httpClientPrivate.get<DiseasesResponseType>(
    `${config.restUrl}/v1/backoffice/e-claim/diseases?page=${query.page}&limit=${query.limit}&search=${query.search}`,
  )

  return response.data
}

const useDiseases = (query: {
  page: number
  limit: number
  search: string
  enabled?: boolean
  key?: any // optional extra key to bust/react to external changes (e.g., claim id)
  debounceMs?: number // optional debounce override per call
}) => {
  // Debounce the search term (default 1000ms; allow override per call)
  const debouncedSearch = useDebounce(query.search, query.debounceMs ?? 1000)

  // Normalize search and guard against empty values to prevent empty calls
  const effectiveSearch = (debouncedSearch || '').trim()
  const isEnabled = Boolean(query.enabled) && effectiveSearch.length > 0

  // Keep the query key minimal and stable (don’t include enabled). Include optional external key.
  const queryKey = [
    'useDiseases',
    query.key ?? null,
    { page: query.page, limit: query.limit, search: effectiveSearch },
  ]

  return useQuery({
    queryKey,
    queryFn: () => fetchDiseases({ page: query.page, limit: query.limit, search: effectiveSearch }),
    enabled: isEnabled,
  })
}

export { useDiseases, fetchDiseases }
